import { useState, useMemo, useEffect, useRef } from "react";
import {
  Search,
  X,
  Play,
  ListPlus,
  Flame,
  Music,
  Film,
  Gamepad2,
  Coffee,
  Tv,
  Check,
  Sparkles,
  Loader2,
  AlertCircle,
} from "lucide-react";
import type { VideoType } from "@/lib/room-state";
import { searchYouTube, isYouTubeSearchEnabled, type YouTubeVideoItem } from "@/lib/youtube-search";

interface YouTubeSearchModalProps {
  isOpen: boolean;
  canControl: boolean;
  onClose: () => void;
  onSelectVideo: (url: string, type: VideoType) => void;
  onAddToQueue: (url: string, title: string) => void;
}

// Curated, embeddable content catalogue. (YouTube Data API needs a key + quota;
// this keeps the feature fully self-contained and free.)
const FEATURED_VIDEOS: YouTubeVideoItem[] = [
  {
    id: "jfKfPfyJRdk",
    title: "Lofi Hip Hop Radio — Beats to Relax/Study to",
    channel: "Lofi Girl",
    thumbnail: "https://i.ytimg.com/vi/jfKfPfyJRdk/hqdefault.jpg",
    duration: "LIVE",
    category: "lofi",
    views: "54k watching",
  },
  {
    id: "8X7N7h_zDyo",
    title: "Synthwave Radio — Chill synth / retro beats",
    channel: "Lofi Girl",
    thumbnail: "https://i.ytimg.com/vi/8X7N7h_zDyo/hqdefault.jpg",
    duration: "LIVE",
    category: "lofi",
    views: "8k watching",
  },
  {
    id: "TcMBFSGVi1c",
    title: "Avengers: Endgame — Official Trailer",
    channel: "Marvel Entertainment",
    thumbnail: "https://i.ytimg.com/vi/TcMBFSGVi1c/hqdefault.jpg",
    duration: "2:26",
    category: "trailers",
    views: "158M views",
  },
  {
    id: "Way9Dexny3w",
    title: "Dune: Part Two — Official Trailer 3",
    channel: "Warner Bros. Pictures",
    thumbnail: "https://i.ytimg.com/vi/Way9Dexny3w/hqdefault.jpg",
    duration: "2:24",
    category: "trailers",
    views: "42M views",
  },
  {
    id: "qEVUtrk8_B4",
    title: "John Wick: Chapter 4 — Official Final Trailer",
    channel: "Lionsgate Movies",
    thumbnail: "https://i.ytimg.com/vi/qEVUtrk8_B4/hqdefault.jpg",
    duration: "1:32",
    category: "trailers",
    views: "34M views",
  },
  {
    id: "d9MyW72ELq0",
    title: "Avatar: The Way of Water — Official Teaser",
    channel: "Avatar",
    thumbnail: "https://i.ytimg.com/vi/d9MyW72ELq0/hqdefault.jpg",
    duration: "1:38",
    category: "trailers",
    views: "52M views",
  },
  {
    id: "dQw4w9WgXcQ",
    title: "Rick Astley — Never Gonna Give You Up",
    channel: "Rick Astley",
    thumbnail: "https://i.ytimg.com/vi/dQw4w9WgXcQ/hqdefault.jpg",
    duration: "3:33",
    category: "music",
    views: "1.5B views",
  },
  {
    id: "fJ9rUzIMcZQ",
    title: "Queen — Bohemian Rhapsody (Remastered)",
    channel: "Queen Official",
    thumbnail: "https://i.ytimg.com/vi/fJ9rUzIMcZQ/hqdefault.jpg",
    duration: "5:59",
    category: "music",
    views: "1.7B views",
  },
  {
    id: "kXYiU_JCYtU",
    title: "Linkin Park — Numb (Official Music Video)",
    channel: "Linkin Park",
    thumbnail: "https://i.ytimg.com/vi/kXYiU_JCYtU/hqdefault.jpg",
    duration: "3:07",
    category: "music",
    views: "2.2B views",
  },
  {
    id: "QdBZY2fkU-0",
    title: "Grand Theft Auto VI — Trailer 1",
    channel: "Rockstar Games",
    thumbnail: "https://i.ytimg.com/vi/QdBZY2fkU-0/hqdefault.jpg",
    duration: "1:31",
    category: "gaming",
    views: "210M views",
  },
  {
    id: "x7Q8UvJ1wvk",
    title: "Elden Ring — Official Gameplay Reveal Trailer",
    channel: "Bandai Namco Entertainment",
    thumbnail: "https://i.ytimg.com/vi/x7Q8UvJ1wvk/hqdefault.jpg",
    duration: "3:00",
    category: "gaming",
    views: "15M views",
  },
  {
    id: "0vhW6H1W9V4",
    title: "Exploring 4K Nature: BBC Planet Earth Highlights",
    channel: "BBC Earth",
    thumbnail: "https://i.ytimg.com/vi/0vhW6H1W9V4/hqdefault.jpg",
    duration: "11:20",
    category: "trending",
    views: "25M views",
  },
  {
    id: "aqz-KE-bpKQ",
    title: "Big Buck Bunny — Full Animated Short Film",
    channel: "Blender Foundation",
    thumbnail: "https://i.ytimg.com/vi/aqz-KE-bpKQ/hqdefault.jpg",
    duration: "10:00",
    category: "trending",
    views: "180M views",
  },
  {
    id: "9bZkp7q19f0",
    title: "PSY — Gangnam Style",
    channel: "officialpsy",
    thumbnail: "https://i.ytimg.com/vi/9bZkp7q19f0/hqdefault.jpg",
    duration: "4:13",
    category: "music",
    views: "5.1B views",
  },
  {
    id: "DWVJ2ApO7Xg",
    title: "Cyberpunk 2077 — Official Trailer (Phantom Liberty)",
    channel: "Cyberpunk 2077",
    thumbnail: "https://i.ytimg.com/vi/DWVJ2ApO7Xg/hqdefault.jpg",
    duration: "2:03",
    category: "gaming",
    views: "35M views",
  },
  {
    id: "QtXby3twMmI",
    title: "Black Myth: Wukong — Official Gameplay Trailer",
    channel: "Game Science",
    thumbnail: "https://i.ytimg.com/vi/QtXby3twMmI/hqdefault.jpg",
    duration: "13:00",
    category: "gaming",
    views: "40M views",
  },
  {
    id: "lTRRU7X2rVQ",
    title: "Interstellar — Official Teaser Trailer",
    channel: "Paramount Pictures",
    thumbnail: "https://i.ytimg.com/vi/lTRRU7X2rVQ/hqdefault.jpg",
    duration: "1:59",
    category: "trailers",
    views: "85M views",
  },
  {
    id: "Q0BZ2nW_Eu0",
    title: "Chillhop Yearmix 2024 — Best Lo-Fi Tracks",
    channel: "Chillhop Music",
    thumbnail: "https://i.ytimg.com/vi/Q0BZ2nW_Eu0/hqdefault.jpg",
    duration: "2:01:33",
    category: "lofi",
    views: "5M views",
  },
];

const CATEGORIES = [
  { id: "all", label: "All", icon: Sparkles },
  { id: "trending", label: "Trending", icon: Flame },
  { id: "trailers", label: "Trailers", icon: Film },
  { id: "music", label: "Music", icon: Music },
  { id: "lofi", label: "Lo-Fi", icon: Coffee },
  { id: "gaming", label: "Gaming", icon: Gamepad2 },
  { id: "tech", label: "Tech & Docs", icon: Tv },
];

export default function YouTubeSearchModal({
  isOpen,
  canControl,
  onClose,
  onSelectVideo,
  onAddToQueue,
}: YouTubeSearchModalProps) {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [addedQueueId, setAddedQueueId] = useState<string | null>(null);
  const [ytResults, setYtResults] = useState<YouTubeVideoItem[] | null>(null);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytError, setYtError] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Real YouTube search (debounced) when API key is configured
  useEffect(() => {
    if (!isYouTubeSearchEnabled) return;
    const cleanQuery = query.trim();
    // Only trigger real search for non-URL queries (URLs use the paste handler)
    if (!cleanQuery || cleanQuery.includes("youtube.com") || cleanQuery.includes("youtu.be") || cleanQuery.startsWith("http")) {
      setYtResults(null);
      setYtError(null);
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setYtLoading(true);
      setYtError(null);
      try {
        const results = await searchYouTube(cleanQuery);
        setYtResults(results);
      } catch (err) {
        setYtError(err instanceof Error ? err.message : "Search failed");
        setYtResults(null);
      } finally {
        setYtLoading(false);
      }
    }, 600);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [query]);

  // Curated catalogue results (used as fallback or when no API key)
  const curatedResults = useMemo(() => {
    const cleanQuery = query.toLowerCase().trim();
    return FEATURED_VIDEOS.filter((video) => {
      const matchCategory = selectedCategory === "all" || video.category === selectedCategory;
      if (!cleanQuery) return matchCategory;
      const matchText =
        video.title.toLowerCase().includes(cleanQuery) ||
        video.channel.toLowerCase().includes(cleanQuery) ||
        video.category.toLowerCase().includes(cleanQuery);
      return matchCategory && matchText;
    });
  }, [query, selectedCategory]);

  // Determine which results to show
  const searchResults = ytResults ?? curatedResults;

  function handlePlayDirect(video: YouTubeVideoItem) {
    if (!canControl) return;
    onSelectVideo(`https://www.youtube.com/watch?v=${video.id}`, "youtube");
    onClose();
  }

  function handleQueue(video: YouTubeVideoItem) {
    onAddToQueue(`https://www.youtube.com/watch?v=${video.id}`, video.title);
    setAddedQueueId(video.id);
    setTimeout(() => setAddedQueueId(null), 2000);
  }

  function handleDirectPaste() {
    const trimmed = query.trim();
    if (!trimmed) return;
    if (trimmed.includes("youtube.com") || trimmed.includes("youtu.be") || trimmed.startsWith("http")) {
      onSelectVideo(trimmed, "youtube");
      onClose();
    }
  }

  if (!isOpen) return null;

  const isYoutubeLink = query.includes("youtube.com") || query.includes("youtu.be");

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-3 sm:p-5 z-50"
      style={{ opacity: 0, animation: "fadeIn 0.15s ease-out forwards" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[86vh] flex flex-col overflow-hidden rounded-xl border border-white/[0.06] shadow-[0_12px_40px_rgba(0,0,0,0.4)]"
        style={{ backgroundColor: "rgba(20, 22, 28, 0.92)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-5 border-b border-white/10 shrink-0 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-white/[0.03] pointer-events-none" />
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
 <div className="w-8 h-8 flex items-center justify-center">
                <Search className="w-3.5 h-3.5 text-primary" />
              </div>
              <h3 className="text-sm font-semibold">Browse Videos</h3>
            </div>
            <button
              onClick={onClose}
 className="w-8 h-8 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-muted-foreground/60 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleDirectPaste();
                  if (e.key === "Escape") onClose();
                }}
                placeholder="Search the catalogue or paste any YouTube URL…"
                autoFocus
                className="w-full rounded-full pl-9 pr-8 py-2.5 text-xs sm:text-sm outline-none border border-white/[0.06]" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {isYoutubeLink && (
              <button
                onClick={handleDirectPaste}
                disabled={!canControl}
                className="px-4 py-2 glass-button-primary text-xs font-semibold rounded-full shrink-0 disabled:opacity-40"
              >
                Load Link
              </button>
            )}
          </div>

          {/* Hint banner */}
          {!isYoutubeLink && (
            <div className="mt-2 text-[10px] text-muted-foreground flex items-center gap-1.5">
              <Search className="w-3 h-3 shrink-0" />
              <span>
                Browse the curated catalogue below, or paste any YouTube URL above and press Enter to load it instantly.
              </span>
            </div>
          )}
          {isYoutubeLink && (
            <div className="mt-2 text-[10px] text-primary flex items-center gap-1.5">
              <Search className="w-3 h-3 shrink-0" />
              <span>YouTube link detected — press Enter or click "Load Link" to play.</span>
            </div>
          )}

          {/* Category pills — hidden during live YouTube search */}
          {!(isYouTubeSearchEnabled && ytResults) && (
          <div className="flex items-center gap-1.5 overflow-x-auto mt-3 no-scrollbar pt-0.5">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isSelected = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all shrink-0 ${
                    isSelected
                      ? "bg-white/[0.08] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                      : "text-muted-foreground hover:text-foreground hover:bg-white/[0.03]"
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4 min-h-0 overscroll-contain">
          {/* Search status banner */}
          {ytLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs">Searching YouTube…</span>
            </div>
          )}
          {ytError && !ytLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="text-xs">{ytError}. Showing curated catalogue instead.</span>
            </div>
          )}
          {!ytLoading && !ytError && isYouTubeSearchEnabled && ytResults && ytResults.length > 0 && (
            <div className="flex items-center gap-1.5 mb-3 text-[10px] text-[#89CFF0]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#89CFF0]" />
              <span>Live YouTube search — {ytResults.length} results</span>
            </div>
          )}
          {!isYouTubeSearchEnabled && query.trim() && !query.includes("http") && (
            <div className="flex items-center gap-1.5 mb-3 text-[10px] text-muted-foreground">
              <AlertCircle className="w-3 h-3 shrink-0" />
              <span>
                Showing curated catalogue. For real YouTube search, set{" "}
                <code className="font-mono bg-secondary px-1 rounded">VITE_YOUTUBE_API_KEY</code>{" "}
                in .env
              </span>
            </div>
          )}
          {!ytLoading && searchResults.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-xs font-medium text-foreground">No matching videos found</p>
              <p className="text-[11px] opacity-70 mt-1 max-w-xs mx-auto">
                Try a different keyword, category, or paste any YouTube URL into the search box.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {searchResults.map((video) => {
                const isJustAdded = addedQueueId === video.id;
                return (
                  <div
                    key={video.id}
                    className="rounded-lg p-3 flex flex-col justify-between transition-all group" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}
                  >
                    <div>
                      <div
                        onClick={() => handlePlayDirect(video)}
                        className="relative aspect-video rounded-lg overflow-hidden bg-black/60 cursor-pointer mb-2 group/thumb"
                      >
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity">
 <div className="w-11 h-11 flex items-center justify-center">
                            <Play className="w-5 h-5 fill-current ml-0.5" />
                          </div>
                        </div>
                        <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5 rounded font-semibold">
                          {video.duration}
                        </span>
                      </div>

                      <h4
                        onClick={() => handlePlayDirect(video)}
                        className="text-xs font-semibold text-foreground clamp-2 leading-snug hover:text-primary cursor-pointer mb-1"
                        title={video.title}
                      >
                        {video.title}
                      </h4>

                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span className="truncate">{video.channel}</span>
                        {video.views && (
                          <span className="shrink-0 font-mono text-[10px] opacity-70">{video.views}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-border/50">
                      <button
                        onClick={() => handlePlayDirect(video)}
                        disabled={!canControl}
                        className="flex-1 flex items-center justify-center gap-1 glass-button-primary disabled:opacity-40 text-xs font-semibold py-2 rounded-full"
                      >
                        <Play className="w-3 h-3 fill-current" />
                        <span>Play</span>
                      </button>
                      <button
                        onClick={() => handleQueue(video)}
                        disabled={isJustAdded}
                        className={`px-3 py-2 text-xs font-semibold rounded-full border transition-all flex items-center gap-1 ${
                          isJustAdded
                            ? "bg-[#89CFF0]/15 text-[#89CFF0] border-[#89CFF0]/30"
                            : "hover:bg-white/[0.05] border-white/[0.06]"
                        }`}
                        style={!isJustAdded ? { backgroundColor: 'rgba(255,255,255,0.02)' } : undefined}
                        title="Add to queue"
                      >
                        {isJustAdded ? (
                          <>
                            <Check className="w-3 h-3 text-[#89CFF0]" />
                            <span className="text-[10px]">Queued</span>
                          </>
                        ) : (
                          <>
                            <ListPlus className="w-3 h-3" />
                            <span className="text-[10px]">Queue</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-3 px-5 border-t border-white/[0.06] flex items-center justify-between text-[11px] shrink-0" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
          <span>Click any video to synchronize across the room instantly.</span>
          <span className="font-mono text-[10px] hidden sm:inline">Press ESC to close</span>
        </div>
      </div>
    </div>
  );
}
